import { createContext, useContext, type ReactNode } from "react";

/**
 * The viewer's IANA timezone, supplied by whichever app tree mounts the
 * provider — NOT read from any auth hook.
 *
 * WHY THIS LIVES IN THE DESIGN SYSTEM. The obvious shape is for `Timestamp`
 * to call the staff app's `useUserTimeZone()` directly. That was built and
 * MEASURED, and it is the wrong fix on four counts:
 *
 *   1. It would be the first app-layer import inside the design system,
 *      which the portal also consumes.
 *   2. `useUserTimeZone` reads the staff `AuthContext`, which resolves to
 *      `undefined` in the portal tree (`main.tsx` renders the portal under
 *      `PortalAuthProvider`, OUTSIDE `AuthProvider`). Output there is
 *      byte-identical to passing nothing — so it fixes the staff sites and
 *      silently does nothing for the portal's, while making every call site
 *      READ as though the viewer's zone resolves everywhere. That is this
 *      repo's green-run-compatible-with-an-inert-fix class.
 *   3. `portal/isolation.test.ts` reads DIRECT import specifiers only, so a
 *      transitive staff-context import from inside the design system is
 *      invisible to it.
 *   4. `useAuth()` THROWS without a provider, so it can never be used here.
 *
 * Inverting the dependency fixes all four: the context lives here, and the
 * app tree that HAS a zone mounts the provider. The staff tree does
 * (`AuthProvider`); the portal tree deliberately does not, because there is
 * nothing to mount it with — `PortalUser` has no timezone column,
 * `PortalMeDto` has no `TimeZone` field, and `portal/api/types.ts` has no
 * such property. The portal therefore stays on `FALLBACK_TIMEZONE`, and
 * that gap is VISIBLE (an absent provider) rather than falsely resolved.
 * Closing it needs a server column first; it is tracked as a cross-tier
 * finding, not a client refactor.
 */
const TimeZoneContext = createContext<string | null>(null);

export interface TimeZoneProviderProps {
  /** The viewer's IANA zone, or null when the tree has none to offer. */
  tz: string | null | undefined;
  children: ReactNode;
}

export function TimeZoneProvider({ tz, children }: TimeZoneProviderProps) {
  return <TimeZoneContext.Provider value={tz ?? null}>{children}</TimeZoneContext.Provider>;
}

/**
 * The ambient viewer zone, or `null` when no provider is mounted.
 *
 * `null` is a FIRST-CLASS return, deliberately not a silent fallback to
 * Phoenix: a caller that gets `null` is being told "this tree has no zone",
 * and the formatters it hands that null to apply `FALLBACK_TIMEZONE`
 * themselves. Returning Phoenix from here instead would make "resolved to
 * the viewer's zone" and "nobody supplied one" indistinguishable at every
 * call site — the exact confusion this module exists to remove.
 */
export function useTimeZone(): string | null {
  return useContext(TimeZoneContext);
}
